import { PayloadAction } from '@reduxjs/toolkit';
import {
  AccountsPageFiltersInitialState,
  accountsStoreKey,
  groupAccontsByMap,
  sortAccountsMap,
} from './accounts.const';
import {
  saveLinks,
  fetchAccounts,
  saveOperationalCost,
  getAccountAndProjectList,
} from './accounts.thunks';
import { AccountOverview, SortOrder } from '../../types';
import {
  AccountsState,
  SortAccountsBy,
  GroupAccountsBy,
  AccountsSortPayloadType,
  AccountsPageFilters,
  saveLinkPayloadType,
} from './accounts.types';
import { createSliceCustom } from '../../utils/tests/createSliceCustom';

export const initialState: AccountsState = {
  loading: false,
  error: null,
  accounts: [],
  groupBy: GroupAccountsBy.AccountName,
  sortBy: SortAccountsBy.Margin,
  sortOrder: SortOrder.Ascending,

  uniqueFilterLists: {
    pdms: [],
    dms: [],
    cps: [],
  },
  isUniqueFilterListsDefined: false,

  dateFilterValue: null,
  dateFilterValueLocal: null,
  filters: AccountsPageFiltersInitialState,
  operationalCost: [],
  accountsList: [],
  projectsList: [],
  projectAndAccountListLoading: false,
};

export const returns = createSliceCustom({
  name: accountsStoreKey,
  initialState,
  reducers: {
    setAccountsPageFilters: (
      state,
      { payload }: PayloadAction<Partial<AccountsPageFilters>>,
    ) => {
      state.filters = { ...state.filters, ...payload };
    },
    resetAccountsPageFilters: (state) => {
      state.filters = initialState.filters;
    },
    setGroupBy: (
      state,
      { payload: groupBy }: PayloadAction<GroupAccountsBy>,
    ) => {
      state.groupBy = groupBy;
      const groupByFn = groupAccontsByMap[groupBy];
      state.accounts = groupByFn(state.accounts);
    },
    setSortBy: (state, { payload }: PayloadAction<AccountsSortPayloadType>) => {
      state.sortBy = payload.sortBy;
      state.sortOrder = payload.sortOrder ?? SortOrder.Ascending;

      const sortingFn = sortAccountsMap[state.sortBy];
      const accounts = state.accounts.sort(sortingFn);

      state.accounts =
        state.sortOrder === SortOrder.Ascending ? accounts : accounts.reverse();
    },

    setDateFilterValueLocal: (
      state,
      { payload }: PayloadAction<string | null>,
    ) => {
      state.dateFilterValueLocal = payload;
    },
    setDateFilterValue: (state) => {
      state.dateFilterValue = state.dateFilterValueLocal;
    },

    cleanDateFilterValues: (state) => {
      state.dateFilterValue = null;
      state.dateFilterValueLocal = null;
    },
    cleanAccountsTableRow: (
      state,
      { payload }: PayloadAction<{ errMessage: string | null }>,
    ) => {
      state.accounts = [];
      state.accountListFetchErrorMessage = payload.errMessage;
    },
  },
  extraReducers: (builder) => {
    // fetch data
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.loading = true;
        state.error = initialState.error;
        state.accountListFetchErrorMessage = null;
      })
      .addCase(
        fetchAccounts.fulfilled,
        (state, { payload }: PayloadAction<AccountOverview[]>) => {
          state.loading = false;

          const sortingFn = sortAccountsMap[state.sortBy];
          const accounts = payload.sort(sortingFn);

          state.accounts =
            state.sortOrder === SortOrder.Ascending
              ? accounts
              : accounts.reverse();
          state.operationalCost = accounts[0]?.operationalCost
            ? accounts[0].operationalCost.sort((a, b) => a.period - b.period)
            : [];

          if (state.isUniqueFilterListsDefined === false) {
            state.isUniqueFilterListsDefined = true;

            state.uniqueFilterLists.pdms = (() => {
              const filterArray: { value: string; label: string }[] = [];
              const uniqueItems: string[] = [];

              accounts.forEach((item) => {
                if (
                  item.pdmId &&
                  item.pdmFullName &&
                  !uniqueItems.includes(item.pdmId)
                ) {
                  uniqueItems.push(item.pdmId);
                  filterArray.push({
                    value: item.pdmId,
                    label: item.pdmFullName,
                  });
                }
              });

              return filterArray.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? ''),
              );
            })();

            state.uniqueFilterLists.dms = (() => {
              const filterArray: { value: string; label: string }[] = [];
              const uniqueItems: string[] = [];

              accounts.forEach((item) => {
                if (
                  item.dmId &&
                  item.dmFullName &&
                  !uniqueItems.includes(item.dmId)
                ) {
                  uniqueItems.push(item.dmId);
                  filterArray.push({
                    value: item.dmId,
                    label: item.dmFullName,
                  });
                }
              });

              return filterArray.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? ''),
              );
            })();

            state.uniqueFilterLists.cps = (() => {
              const filterArray: { value: string; label: string }[] = [];
              const uniqueItems: string[] = [];

              accounts.forEach((item) => {
                if (
                  item.cpId &&
                  item.cpFullName &&
                  !uniqueItems.includes(item.cpId)
                ) {
                  uniqueItems.push(item.cpId);
                  filterArray.push({
                    value: item.cpId,
                    label: item.cpFullName,
                  });
                }
              });

              return filterArray.sort((a, b) =>
                (a.label ?? '').localeCompare(b.label ?? ''),
              );
            })();
          }
        },
      )
      .addCase(fetchAccounts.rejected, (state) => {
        state.loading = false;
        state.error = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(
        saveLinks.fulfilled,
        (state, { payload }: PayloadAction<saveLinkPayloadType>) => {
          state.accounts = state.accounts.map((account) => {
            if (account.id === payload.accountId) {
              return {
                ...account,
                compensationToolLink: payload.compensationToolLink ?? '',
                sharedDriveLink: payload.sharedDriveLink ?? '',
                customLinks: payload.customLinks ?? [],
              };
            } else {
              return account;
            }
          });
        },
      )
      .addCase(saveLinks.rejected, (state) => {
        state.error = {
          message: `Oops. Something went wrong. :(`,
        };
      })
      .addCase(saveOperationalCost.fulfilled, (state, { payload }) => {
        if (!payload) return;

        if (!state.operationalCost) {
          state.operationalCost = [payload];
          return;
        }

        const updatedOperationalCostIndex = state.operationalCost.findIndex(
          (operationalCost) => operationalCost.period === payload.period,
        );

        if (updatedOperationalCostIndex === -1) {
          state.operationalCost.push(payload);
          return;
        }

        state.operationalCost[updatedOperationalCostIndex] = payload;
      })
      .addCase(getAccountAndProjectList.pending, (state) => {
        state.projectAndAccountListLoading = true;
      })
      .addCase(getAccountAndProjectList.fulfilled, (state, { payload }) => {
        state.projectAndAccountListLoading = false;
        state.accountsList = payload.accounts;
        state.projectsList = payload.projects;
      })
      .addCase(getAccountAndProjectList.rejected, (state) => {
        state.projectAndAccountListLoading = false;
      });
  },
});

export const accountsSlice = returns.slice;
export const accountsReducers = returns.reducers;
export const accountsExtraReducers = returns.extraReducers;
